<template>
  <div
    role="application"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @mousedown="handleMouseDown"
    @mousemove="handleMouseMove"
    @mouseup="handleMouseUp"
    @wheel.prevent="handleWheel"
  >
    <krpano
      v-if="isKrpanoActive"
      ref="krpano"
      class="krpano-wrapper"
      xml=""
      html5="webgl+only"
      mwheel
      pass-query-parameters
      lazy-load
      :webglsettings="{ depth: true, webgl2: true }"
      :hooks="hooks"
      :focus="false"
      @panoCreated="krpanoInit"
    >
      <!--focus=false: avoid when krpano loaded, will auto scroll and focus on the iframe-->
    </krpano>
    <!--<div-->
    <!--v-if="isKrpanoActive"-->
    <!--id="pano"-->
    <!--class="krpano-wrapper">-->
    <!--<noscript>-->
    <!--<table style="width:100%;height:100%;">-->
    <!--<tr style="vertical-align:middle;">-->
    <!--<td>-->
    <!--<div style="text-align:center;">ERROR:<br/><br/>Javascript not activated<br/><br/></div>-->
    <!--</td>-->
    <!--</tr>-->
    <!--</table>-->
    <!--</noscript>-->
    <!--</div>-->
  </div>
</template>

<script>
import _, { cloneDeep, has } from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { locale } from '../../../i18n/i18n-helper'
import getActionsXml from './xml/actions'
import eventsXml from './xml/events'
import getStylesXml from './xml/styles'
import hooks from './hooks'
import getScenesXml from './xml/scenes'
import getLogoTripodXml from './xml/tripod'
import { contextMenuXml, gyroMessageXml, gyroXml, threeJsXml, webVRXml } from './xml/plugins'
import {
  getIEVersion,
  isEmpty,
  PromiseService,
  isMobile,
  processVideoUrl
} from '@/api/utils'
import {
  convertIndexFromArrayToUrl,
  convertIndexFromUrlToArray,
  returnProcessedContent,
  canLaunchPlanetView,
  isUrlGif,
  xmlUrlString
} from '@/api/helpers'
import visitorApi from '@/services/api/visitor'
import gaEvents from '@/api/ga-events'
import { HOTSPOT_BASE_SIZE } from '@/api/constants'
import { ARVIEWER_QUERIES } from '@/api/armaker'
import gifFrames from 'gif-frames'
import { resizeImage } from '@/utils/resize'

export default {
  name: 'KrpanoIndex',
  props: {
    isMouseOnMarkerInfo: Boolean,
    isMouseOnKrpanoMarker: Boolean,
    mouseenterMarker: {
      type: Function,
      default: () => {}
    },
    mouseleaveMarker: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      defaultFov: 120,
      krpanoVrModeObj: {
        vrModeShouldHide: [],
        vrModeShouldShow: ['vr_menu_bg', 'vr_menu_l', 'vr_menu', 'vr_menu_r']
      },
      mouseEvent: {
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0
      },
      touchEvent: {
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0
      },
      vrThumbAth: 24,
      vrThumbWidth: 160,
      firstLoad: true, // firstLoad will launch planet view
      waitingTimeout: null,
      videoPlayPaused: {},
      syncingCamera: false,
      dragging: false,
      changeCameraInterval: null
    }
  },
  computed: {
    ...mapGetters([
      'currentBuilding',
      'currentPanorama',
      'customSetting',
      'headquarter',
      'isCommentsActive',
      'isGyroEnabled',
      'isFloorplanActive',
      'isKrpanoActive',
      'isInformationActive',
      'isMarkerInfoActive',
      'isPanoramasListActive',
      'isShareActive',
      'isCameraRotating',
      'isUiMode',
      'isVrMode',
      'krpanoEl',
      'krpanoHooks',
      'krpanoLookAtH',
      'krpanoXmlPlugins',
      'krpanoXOffset',
      'logoSize',
      'panoramas',
      'markers'
    ]),

    hooks () {
      // startAutoRotate, stopAutoRotate , setProgressCount, setProgressMax, closeProgress, showProgress, setMarkerInfo, setKrpanoLookAtH, exitVrMode
      return hooks(this, this.$store)
    },

    logoTripod () {
      if (!this.isUiMode) {
        if (this.customSetting.customBranding) {
          return this.headquarter.tripodUrl ||
            this.currentBuilding.logo ||
            ''
        } else {
          return require('./img/logo-tripod.png')
        }
      }
      return ''
    },
    clonedPanoramas () {
      const startIndex = convertIndexFromUrlToArray(this.$route.query.index, this.panoramas.length)
      const clonedPanoramas = cloneDeep(this.panoramas)
      const startUpPanorama = clonedPanoramas.splice(startIndex, 1)
      clonedPanoramas.unshift(...startUpPanorama)
      return clonedPanoramas
    },
    gifInfosMap () {
      return this.clonedPanoramas.reduce((acc, panorama) => {
        const markers = panorama.markers
        const panoId = panorama.objectId
        const value = []
        if (!acc.get(panoId)) {
          acc.set(panoId, [])
        }

        markers.forEach((marker) => {
          const isGif = isUrlGif(marker.iconUrl)
          if (marker.useCustomIcon && isGif) {
            const tagSizeRatio = marker.tagsizePercent / 100
            const objectId = marker.objectId
            const name = marker.name
            const url = marker.disableResizing ? marker.iconUrl : resizeImage(marker.iconUrl)
            const type = marker.type
            value.push({ type, tagSizeRatio, objectId, name, url })
          }
        })
        return acc.set(panoId, value)
      }, new Map())
    },
    imageInfosMap () {
      return this.clonedPanoramas.reduce((acc, panorama) => {
        const markers = panorama.markers
        const panoId = panorama.objectId
        const value = []
        if (!acc.get(panoId)) {
          acc.set(panoId, [])
        }

        markers.forEach((marker) => {
          if (marker.photo && !marker.images) {
            const objectId = marker.objectId
            const url = marker.photo
            const type = marker.type
            const name = marker.name
            value.push({ type, objectId, url, name })
          }
        })
        return acc.set(panoId, value)
      }, new Map())
    }
  },

  watch: {
    isKrpanoActive (bool = false) {
      if (bool === false) {
        removepano(this.krpanoEl.id) // eslint-disable-line
      }
    },
    isCameraRotating (val) {
      if (val) {
        this.startSyncingCamera()
      } else {
        this.stopSyncingCamera()
      }
    },
    currentPanorama () {
      window.krpanoObj.call('change_camera();')
    }
  },

  beforeMount () {
    this.initGyroFromIframe()
    for (let i = 0; i < this.panoramas.length; i++) {
      this.krpanoVrModeObj.vrModeShouldShow.push(`vr_panorama_${i}`)
      this.krpanoVrModeObj.vrModeShouldShow.push(`vr_panorama_text_${i}`)
    }
  },

  mounted () {
    window.addEventListener('keydown', this.keydownHandler)
    this.subscribeEvent()
    this.openSpecificVideo()
    this.showIntro()
    // setTimeout(() => {
    //   const vm = this
    //   embedpano({ // eslint-disable-line
    //     id: 'krpano_' + Math.floor(Math.random() * (100000 - 100 + 1) + 100),
    //     xml: '',
    //     target: 'pano',
    //     html5: 'prefer',
    //     mwheel: true,
    //     initvars: { design: 'flat' },
    //     passQueryParameters: true,
    //     onready (krpanoObj) {
    //       krpanoObj.hooks = vm.hooks
    //       vm.krpanoInit(krpanoObj)
    //     }
    //   })
    // }, 1000)
  },

  beforeDestroy () {
    this.stopSyncingCamera()
    window.removeEventListener('keydown', this.keydownHandler)
  },

  methods: {
    ...mapGetters([
      'markerGifInfos',
      'markerImageInfos'
    ]),
    ...mapActions([
      'closePilotTooltip',
      'closeComments',
      'closeFloorplan',
      'closeInformation',
      'closeMarkerInfo',
      'closeMobileMenu',
      'closePanoramasList',
      'closeProgress',
      'closeShare',
      'exitVrMode',
      'initGyroFromIframe',
      'setKrpanoEl',
      'setKrpanoCamera',
      'setMarkerInfoData',
      'setPanorama',
      'setPopupSizeConfig',
      'setPopupUrl',
      'setPopupMarker',
      'setProgressCount',
      'setProgressMax',
      'showMarkerInfo',
      'showProgress',
      'showPopup',
      'closePopup',
      'startAutoRotate',
      'stopAutoRotate',
      'startGyro',
      'toggleGyro',
      'selectBuilding',
      'pauseAudio',
      'pauseMusic',
      'pauseAllVideo',
      'realPausePilotPlaying',
      'setSelectedCategory',
      'setSearchKeywords',
      'initSearch',
      'showSearchs',
      'closeMobileMenu',
      'closeShare',
      'closeMarkerInfo',
      'closeLocation',
      'closeComments',
      'closeFloatMenu'
    ]),
    ...mapActions({ updateIsMoving: 'vrlounge/updateIsMoving' }),
    ...mapMutations([
      'SET_MARKER_GIF_INFOS',
      'SET_MARKER_IMAGE_INFOS'
    ]),
    async setMarkerImageInfos () {
      const panoId = this.currentPanorama.objectId
      const infos = this.markerImageInfos()
      if (infos.has(panoId) === false) {
        const result = new Map()
        const promiseMap = this.imageInfosMap.get(panoId).map(async (img) => {
          const imgSize = await this.getImageOriginalSize(img.url)
          result.set(img.objectId, { ...img, ...imgSize })
        })
        await Promise.all(promiseMap)
        infos.set(panoId, result)
        this.SET_MARKER_IMAGE_INFOS(infos)
      }
    },
    async convertCanvasToBlob (canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            let url = URL.createObjectURL(blob)
            url = xmlUrlString(url)
            resolve(url)
          } else {
            reject(new Error('fail'))
          }
        }, 'image/png')
      })
    },
    async convertGifToSprite () {
      try {
        const panoId = this.currentPanorama.objectId
        const infos = this.markerGifInfos()
        if (infos.has(panoId) === false) {
          const result = new Map()
          const promiseMap = this.gifInfosMap.get(panoId).map(async (gif) => {
            const frames = await this.getFrames(gif)
            if (frames.length === 0) { throw new Error('No frames found!') }
            const processedFrames = this.renderCumulativeFrames(frames)
            const gifSize = await this.getImageOriginalSize(gif.url)
            const frameInfo = this.getFrameInfo(processedFrames, gifSize)
            const canvas = this.createCanvas(frameInfo)
            this.setContext2D(canvas, processedFrames, frameInfo)

            result.set(gif.objectId, { ...gif, ...frameInfo, blob: await this.convertCanvasToBlob(canvas) })
          })
          await Promise.all(promiseMap)
          infos.set(panoId, result)
          this.SET_MARKER_GIF_INFOS(infos)
        }
      } catch (error) {
        console.error('Error processing GIF:', error)
      }
    },
    getFrames (gif) {
      return gifFrames({
        url: gif.url,
        frames: 'all',
        outputType: 'canvas',
        cumulative: false
      })
    },
    renderCumulativeFrames (frameData) {
      if (frameData.length === 0) {
        return frameData
      }
      const previous = document.createElement('canvas')
      const previousContext = previous.getContext('2d')
      const current = document.createElement('canvas')
      const currentContext = current.getContext('2d')

      // Setting the canvas width will clear the canvas, so we only want to do it once.
      const firstFrameCanvas = frameData[0].getImage()

      // It also apperas that 'gif-frames' always returns a consistent sized canvas for all frames.
      previous.width = firstFrameCanvas.width
      previous.height = firstFrameCanvas.height
      current.width = firstFrameCanvas.width
      current.height = firstFrameCanvas.height
      for (const frame of frameData) {
        // Copy the current to the previous.
        previousContext.clearRect(0, 0, previous.width, previous.height)
        previousContext.drawImage(current, 0, 0)

        // Draw the current frame to the cumulative buffer.
        const canvas = frame.getImage()
        const context = canvas.getContext('2d')
        currentContext.drawImage(canvas, 0, 0)
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.drawImage(current, 0, 0)

        const { frameInfo } = frame
        const { disposal } = frameInfo
        // If the disposal method is clear to the background color, then clear the canvas.
        if (disposal === 2) {
          currentContext.clearRect(frameInfo.x, frameInfo.y, frameInfo.width, frameInfo.height)
          // If the disposal method is reset to the previous, then copy the previous over the current.
        } else if (disposal === 3) {
          currentContext.clearRect(0, 0, current.width, current.height)
          currentContext.drawImage(previous, 0, 0)
        }
        // Rewrite function, get processed canvas instead of the original one.
        frame.getImage = () => canvas
      }
      return frameData
    },
    async getImageOriginalSize (url) {
      const imgSize = { width: 0, height: 0 }
      await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          imgSize.width = img.width
          imgSize.height = img.height
          resolve('Image loading is done when fire getImageOriginalSize()')
        }
        img.onerror = () => {
          reject(new Error('Image loading has something wrong when fire getImageOriginalSize()'))
        }
        img.src = url
      })
      return imgSize
    },
    getFrameInfo (frames, size) {
      const frameLength = frames.length
      const frameWidth = size.width
      const frameHeight = size.height
      const ROW = 6
      const duration = frames.reduce((acc, frame) => {
        acc += frame.frameInfo.delay
        return acc
      }, 0)
      return { frameLength, frameWidth, frameHeight, ROW, duration: duration / 100 }
    },
    createCanvas (frameInfo) {
      const { frameLength, frameWidth, frameHeight, ROW } = frameInfo
      const canvasWidth = frameWidth * ROW
      const canvasHeight = frameHeight * Math.ceil(frameLength / ROW)
      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      return canvas
    },
    setContext2D (canvas, frames, frameInfo) {
      const { frameWidth, frameHeight, ROW } = frameInfo
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Failed to get 2D context')
      }
      frames.forEach((frame, index) => {
        const image = frame.getImage()
        const x = (index % ROW) * frameWidth
        const y = Math.floor(index / ROW) * frameHeight
        ctx.drawImage(image, x, y)
      })
    },
    getKrpanoXml () {
      if (this.panoramas.length === 0) {
        return ''
      }
      const startIndex = convertIndexFromUrlToArray(this.$route.query.index, this.panoramas.length)
      const stylesXml = getStylesXml(this.panoramas, this.vrThumbAth, this.vrThumbWidth)
      const scenesXml = getScenesXml(this.panoramas, startIndex, this.krpanoXOffset, this.customSetting, this.krpanoVrModeObj, this.nextPanoramaCategoryName, this.defaultFov)
      const actionsXml = getActionsXml(startIndex, this.panoramas, this.defaultFov, this.krpanoXOffset, this.vrThumbAth, this.krpanoVrModeObj)
      const compassConfig = {
        compass: this.currentBuilding.compass,
        firstCompassDegree: (this.panoramas[startIndex].compassDegree !== undefined) ? (this.krpanoXOffset - (this.panoramas[startIndex].compassDegree)) : undefined,
        locale: locale()
      }
      const logoTripodXml = getLogoTripodXml(this.logoTripod, this.logoSize, this.panoramas[startIndex].isTopLogo, compassConfig)
      const xml = `<krpano onstart="startup(${this.firstLoad && canLaunchPlanetView()})">
      ${webVRXml}
      ${gyroXml}
      ${gyroMessageXml}
      ${contextMenuXml}
      ${logoTripodXml}
      ${eventsXml}
      ${stylesXml}
      ${scenesXml}
      ${actionsXml}
      ${!getIEVersion() ? threeJsXml : ''}</krpano>`
      return xml
    },

    krpanoInit (krpanoObj = {}) {
      window.krHooks = krpanoObj.hooks
      window.krpanoObj = krpanoObj
      this.setKrpanoEl(this.$refs.krpano.$el)
      krpanoObj.call(`loadxml(${escape(this.getKrpanoXml())})`)
      this.startSyncingCamera()
      this.firstLoad = false
    },

    startSyncingCamera () {
      clearInterval(this.changeCameraInterval)
      this.syncingCamera = true
      this.changeCameraInterval = setInterval(() => {
        // get krpano camera hlookat, vlookat and fov
        window.krpanoObj.call('change_camera();')
      }, 100)
    },

    stopSyncingCamera () {
      clearInterval(this.changeCameraInterval)
      this.syncingCamera = false
    },

    subscribeEvent () {
      this.krpanoHooks.on('point_click', (markerIndex) => {
        this.trackClickEvent(markerIndex)
        this.mouseleaveMarker()
      })
      this.krpanoHooks.on('teleport_click', (markerIndex) => {
        this.trackClickEvent(markerIndex)
        this.mouseleaveMarker()
      })
      this.krpanoHooks.on('startup', () => {
        this.hooks.startAutoRun()
        this.$emit('startup')
      })
      this.krpanoHooks.on('change_scene', (revert) => this.hooks.startAutoRun(revert))
      this.krpanoHooks.on('fullscreen_videoplay', (markerId) => {
        const marker = this.markers.find(marker => marker.objectId === markerId)
        this.mouseenterMarker(marker)
        this.$emit('launch-videoplay-fullscreen')
      })
      this.krpanoHooks.on('on_video_start', (markerId) => {
        this.pauseAudio()
        this.pauseMusic()
        this.pauseAllVideo()
        this.realPausePilotPlaying()
      })
      this.krpanoHooks.on('on_video_play', (markerId) => {
        const marker = this.markers.find(marker => marker.objectId === markerId)
        const markerName = has(marker, ['nameMulti', this.$i18n.locale])
          ? marker.nameMulti[this.$i18n.locale]
          : marker.name
        if (!this.videoPlayPaused[markerId]) {
          gaEvents.sendEvent('Marker', 'VideoClicks', markerId)
          this.$mixpanel.track('Click Marker', {
            buildingId: this.currentBuilding.objectId,
            objectId: markerId,
            type: 'videoplay',
            name: markerName
          })
          visitorApi.addClickCount(markerId)
        }
        // Video 第一次被播放：this.videoPlayPaused[markerId] === undefined
        // Video 從暫停開始播放：this.videoPlayPaused[markerId] === true
        if (this.videoPlayPaused[markerId] === undefined || this.videoPlayPaused[markerId] === true) {
          const options = {
            video_title_istaging: markerName,
            video_url_istaging: 'Undefined'
          }
          gaEvents.sendDataLayer('istaging_video', options)
        }
        this.videoPlayPaused[markerId] = false
      })
      this.krpanoHooks.on('on_video_paused', (markerId) => {
        this.videoPlayPaused[markerId] = true
      })
      this.krpanoHooks.on('on_video_complete', (markerId) => {
        this.videoPlayPaused[markerId] = false
        const marker = this.markers.find(marker => marker.objectId === markerId)
        if (marker) {
          // Manual replay when ended
          window.krpanoObj.call(`hotspot[marker_${markerId}].play()`)
        }
      })
      this.krpanoHooks.on('on_embed_video_click', (markerId) => {
        if (markerId && markerId !== '') {
          const marker = this.markers.find(marker => marker.objectId === markerId)
          const markerName = has(marker, ['nameMulti', this.$i18n.locale])
            ? marker.nameMulti[this.$i18n.locale]
            : marker.name
          const options = {
            video_title_istaging: markerName,
            video_url_istaging: marker.videoplayAttribute.embedUrl
          }
          gaEvents.sendDataLayer('istaging_video', options)
        }
      })
      this.krpanoHooks.on('hotspot_loaded', (markerId) => {
        const marker = this.markers.find(marker => marker.objectId === markerId)
        const isVideo = marker.type === 'videoplay'
        const markerNamePrefix = isVideo ? 'markerInfo_preview_' : 'marker_'
        const img = window.krpanoObj.get(`hotspot[${markerNamePrefix}${markerId}].loader`)
        if (img && img.height > img.width) {
          /**
           * landscape (default): width=HOTSPOT_BASE_SIZE, height=auto
           * portrait: width=auto, height=HOTSPOT_BASE_SIZE
           */
          const height = HOTSPOT_BASE_SIZE * (isVideo ? 10 : 1)
          window.krpanoObj.call(`
            set(hotspot[marker_${markerId}].width, 'prop');
            set(hotspot[marker_${markerId}].height, ${height});`)
          if (isVideo) {
            window.krpanoObj.call(`
              set(hotspot[markerInfo_preview_${markerId}].width, 'prop');
              set(hotspot[markerInfo_preview_${markerId}].height, ${height});`)
          }
        }
      })
    },

    nextPanoramaCategoryName (marker = {}, xmlEncode = true, locale = '') {
      if (!this.panoramas || this.panoramas.length <= 0) {
        return
      }
      const foundPanorama = this.panoramas.find(panorama =>
        panorama.objectId === marker.nextPanoramaId
      )
      if (isEmpty(foundPanorama)) {
        return
      }
      return this.getPanoramaCategoryName(foundPanorama, xmlEncode, locale)
    },

    getPanoramaCategoryName (panorama = {}, xmlEncode = true, locale = '') {
      const name = panorama.customCategory || this.$t(panorama.category, locale)
      return xmlEncode ? returnProcessedContent(name) : name
    },

    trackClickEvent (index) {
      const markers = this.currentPanorama.markers || []
      const marker = markers[index]
      if (!marker) {
        return
      }
      let name = ''
      if (marker.type === 'point') {
        name = this.nextPanoramaCategoryName(marker, false, 'en')
      } else if (marker.type === 'teleport') {
        name = this.getPanoramaCategoryName(marker.teleportPanorama, false, 'en')
      }
      const payload = {
        buildingId: marker.buildingId,
        objectId: marker.objectId,
        nextPanoramaId: marker.nextPanoramaId,
        type: marker.type,
        name
      }
      let markerTypeText = ''
      if (marker.type === 'point') {
        markerTypeText = 'Point'
      } else if (marker.type === 'teleport') {
        markerTypeText = 'Teleport'
        payload.nextBuildingId = marker.nextBuildingId
      }
      this.$mixpanel.track('Click ' + markerTypeText, payload)
    },

    async selectPanorama (nextPanoramaId = '', selectedMethod = '', isMarkerPoint = false, isWebVr = false) {
      let index = this.panoramas.findIndex(panorama =>
        panorama.objectId === nextPanoramaId
      )
      const panorama = this.panoramas[index]
      panorama.isFromMarkerPoint = isMarkerPoint
      index = convertIndexFromArrayToUrl(index, this.panoramas.length)
      const categoryMulti = JSON.parse(panorama.categoryMulti || '""')

      const pageInfoMovingIstaging = has(categoryMulti, [this.$i18n.locale])
        ? categoryMulti[this.$i18n.locale]
        : panorama.category

      const sendGtmEvent = (navigationtype) => {
        const options = {
          navigationtype,
          page_info_moving_istaging: pageInfoMovingIstaging
        }
        gaEvents.sendDataLayer('istaging_moving', options, true)
      }

      if (selectedMethod) {
        switch (selectedMethod) {
          case 'Screen':
            gaEvents.sendEvent('Panorama', 'ClickScreenOnPanorama', nextPanoramaId)
            break
          case 'VrMode':
            gaEvents.sendEvent('Panorama', 'ClickDotInVRView', nextPanoramaId)
            break
          case 'VrModePrev':
            gaEvents.sendEvent('Panorama', 'ClickPrevInVRView', nextPanoramaId)
            break
          case 'VrModeNext':
            gaEvents.sendEvent('Panorama', 'ClickNextInVRView', nextPanoramaId)
            break
          case 'VrModeThumbnail':
            gaEvents.sendEvent('Panorama', 'ClickNextThumbnailInVRVIEW', nextPanoramaId)
            break
          case 'VrModeThumbnailText':
            gaEvents.sendEvent('Panorama', 'ClickNextThumbnailTextInVRVIEW', nextPanoramaId)
            break
          case 'Hotspot':
            sendGtmEvent('arrow')
            if (isMarkerPoint) {
              if (isWebVr) {
                gaEvents.sendEvent('Panorama', 'ClickDotInVRView', nextPanoramaId)
              } else {
                gaEvents.sendEvent('Panorama', 'ClickDotInPanorama', nextPanoramaId)
              }
            }
            break
          case 'PanoramaList':
            sendGtmEvent('pannel')
            gaEvents.sendEvent('Panorama', 'ClickInPanoramaList', nextPanoramaId)
            break
          case 'YcPanoramaList':
            gaEvents.sendEvent('Panorama', 'SelectYCInPanoramaList', nextPanoramaId)
            break
          case 'FloorplanDots':
            sendGtmEvent('map')
            gaEvents.sendEvent('Panorama', 'ClickDotOnFloorPlan', nextPanoramaId)
            break
        }
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          index
        }
      })
      this.setPanorama(panorama)
      gaEvents.sendSceneView()
    },

    handleShowPopup (index = 0) {
      this.waitForMarkerResponse(index, marker => {
        const {
          objectId,
          buildingId,
          type,
          name,
          nameMulti,
          action,
          actionLink,
          actionLinkMulti,
          widthPercent
        } = marker
        const markerName = has(nameMulti, [this.$i18n.locale])
          ? nameMulti[this.$i18n.locale]
          : name
        this.$mixpanel.track('Click Marker', {
          buildingId,
          objectId,
          type,
          name: markerName
        })
        if (marker.type === 'pdf') {
          const options = { document_title_istaging: markerName }
          gaEvents.sendDataLayer('istaging_document', options)
        }
        gaEvents.sendEvent('Marker', 'LinkClicks', objectId)
        visitorApi.addClickCount(objectId)
        const i18nLink = actionLinkMulti ? actionLinkMulti[this.$i18n.locale] : ''
        const isAutoDetectMobileAction = action === 'auto' && isMobile()
        const isAutoDetectPCAction = action === 'auto' && !isMobile()
        // pdf maker open link on mobile instead of embedded iframe
        // This is to avoid a bug where iOS would render an image instead of a pdf.
        const isAutoDetectPdfMobileAction = marker.type === 'pdf' && isMobile()
        let link = i18nLink || actionLink
        let transparent = false
        if (marker.type === 'arMakerTag') {
          const disableBackground = marker.customAttribute
            ? marker.customAttribute.disableBackground
            : false
          if (disableBackground) {
            link += (link.includes('?') ? '&' : '?') + ARVIEWER_QUERIES.noBackground
            transparent = true
          }
        }
        if (action === 'redirect') {
          window.location.href = link
          return
        }
        if (action === 'window' || isAutoDetectMobileAction || isAutoDetectPdfMobileAction) {
          window.open(link)
          return
        }
        if (isAutoDetectPCAction) {
          link = processVideoUrl(link)
        }
        this.setPopupUrl(link)
        this.setPopupMarker(marker)
        this.setPopupSizeConfig({
          widthPercent,
          transparent
        })
        this.showPopup()
        this.pauseAudio() // to avoid the iframe sound confict with bgm
      })
    },

    krpanoMarkerMousein (index = 0, mouseX = 0, mouseY = 0) {
      this.waitForMarkerResponse(index, marker => {
        this.$emit('setIsMouseOnKrpanoMarker', true)
        this.$emit('setMarkerPositionX', mouseX)
        this.$emit('setMarkerPositionY', mouseY)
        this.mouseenterMarker(marker)
      })
    },

    krpanoMarkerMouseout (index = 0) {
      this.$emit('setIsMouseOnKrpanoMarker', false)
      this.$nextTick(() => {
        if (this.isMouseOnMarkerInfo === false && this.isMouseOnKrpanoMarker === false) {
          this.mouseleaveMarker()
        }
      })
    },

    setMarkerInfo (index = 0) {
      this.waitForMarkerResponse(index, marker => {
        this.showMarkerInfo()
        this.setMarkerInfoData(marker)
      })
    },

    setMarkerNarration (index = 0) {
      this.waitForMarkerResponse(index, marker => {
        const type = _.get(marker, 'type')
        if (type !== 'narration') return

        this.$emit('hook:narration', marker)
      })
    },

    setSearchSetting (index = 0) {
      this.waitForMarkerResponse(index, async marker => {
        const type = _.get(marker, 'type')
        if (type !== 'search') return
        if (marker.customAttribute && marker.customAttribute.category && marker.customAttribute.keywords) {
          await this.initSearch()
          this.showSearchs()
          this.closeMobileMenu()
          this.closeShare()
          this.closeMarkerInfo()
          this.closeLocation()
          this.closeComments()
          this.setSelectedCategory(marker.customAttribute.category)
          this.setSearchKeywords(marker.customAttribute.keywords)
        }
      })
    },

    waitForMarkerResponse (index, onComplete) {
      clearTimeout(this.waitingTimeout)
      const marker = this.markers[index]
      if (marker) {
        if (typeof onComplete === 'function') {
          onComplete(marker)
        }
      } else {
        this.waitingTimeout = setTimeout(() => {
          this.waitForMarkerResponse(index, onComplete)
        }, 200)
      }
    },

    keydownHandler (e) {
      // arrow key up, down, left, right
      if (e.keyCode === 37 ||
        e.keyCode === 38 ||
        e.keyCode === 39 ||
        e.keyCode === 40) {
        this.stopAutoRotate({
          shouldAutoStartRotate: !this.isVrMode && !this.isGyroEnabled,
          stopMethod: 'Keyboard'
        })
      }
    },

    handleTouchStart (e = null) {
      if (!e) {
        return
      }
      this.stopAutoRotate({
        shouldAutoStartRotate: false,
        stopMethod: 'Touch'
      })
      this.touchEvent.startX = e.touches[0].pageX
      this.touchEvent.startY = e.touches[0].pageY
      this.touchEvent.endX = e.touches[0].pageX
      this.touchEvent.endY = e.touches[0].pageY
      this.updateIsMoving(true)
      this.dragging = true
    },

    handleTouchMove (e = null) {
      if (!e) {
        return
      }
      this.touchEvent.endX = e.touches[0].pageX
      this.touchEvent.endY = e.touches[0].pageY
      if (this.dragging && !this.syncingCamera) {
        this.startSyncingCamera()
      }
    },

    handleTouchEnd () {
      this.stopAutoRotate({
        shouldAutoStartRotate: !this.isVrMode && !this.isGyroEnabled
      })
      const movingX = Math.abs(this.touchEvent.startX - this.touchEvent.endX)
      const movingY = Math.abs(this.touchEvent.startY - this.touchEvent.endY)
      const movingOffset = 20
      if (movingX <= movingOffset && movingY <= movingOffset) {
        this.mobileCloseAll()
      }
      this.updateIsMoving(false)
      this.dragging = false
      this.stopSyncingCamera()
    },

    handleMouseDown (e = null) {
      if (!e) {
        return
      }
      this.stopAutoRotate({
        shouldAutoStartRotate: false,
        stopMethod: 'Mouse'
      })
      this.mouseEvent.startX = e.clientX
      this.mouseEvent.startY = e.clientY
      this.mouseEvent.endX = e.clientX
      this.mouseEvent.endY = e.clientY
      this.dragging = true
    },

    handleMouseMove (e = null) {
      if (!e) {
        return
      }
      this.mouseEvent.endX = e.clientX
      this.mouseEvent.endY = e.clientY
      if (window.krpanoObj) {
        window.krpanoObj.call('hover_closest_point_marker();')
      }
      if (this.dragging && !this.syncingCamera) {
        this.startSyncingCamera()
      }
    },

    handleMouseUp () {
      this.stopAutoRotate({
        shouldAutoStartRotate: !this.isVrMode && !this.isGyroEnabled
      })
      const movingX = Math.abs(this.mouseEvent.startX - this.mouseEvent.endX)
      const movingY = Math.abs(this.mouseEvent.startY - this.mouseEvent.endY)
      const movingOffset = 0
      if (movingX <= movingOffset && movingY <= movingOffset) {
        this.pcCloseAll()
      }
      this.dragging = false
      this.stopSyncingCamera()
    },

    handleWheel () {
      window.krpanoObj.call('change_camera();')
    },

    mobileCloseAll () {
      this.closePilotTooltip()
      this.closeFloatMenu()
      this.closeMobileMenu()
      this.closeShare()
      this.closePanoramasList()
      this.closeFloorplan()
    },

    pcCloseAll () {
      this.closePilotTooltip()
      this.closeFloatMenu()
      this.closeInformation()
      this.closeComments()
      this.closeShare()
      // 768 = CSS assets/css/variables.styl $response value
      if (window.innerWidth < 768) {
        this.closeMobileMenu()
        this.closePanoramasList()
        this.closeFloorplan()
      }
      if (this.$route.name === 'yung-ching') {
        this.closePanoramasList()
      }
    },

    changeBuilding (nextBuildingId, nextBuildingIndex = 1, objectId) {
      gaEvents.sendEvent('Marker', 'TeleportClicks', objectId)
      this.selectBuilding({ buildingId: nextBuildingId, index: nextBuildingIndex })
    },

    openSpecificVideo () {
      // Just for a customer who give us NT$ 90,000.
      // When it is determined that it will no longer be used in the future, it can be deleted.
      let allMarkers = []
      _.forEach(this.panoramas, pano => {
        allMarkers = allMarkers.concat(pano.markers)
      })
      if (_.get(this.currentBuilding, 'objectId', '') === 'a4e5af23-e806-4a9e-bf6b-c432ba846cf3') {
        const specificMarker = _.find(allMarkers, marker =>
          _.get(marker, 'objectId', '') === '9fb2b563-01f2-4ec7-86d8-dc8c36b399aa')
        const { widthPercent } = specificMarker
        specificMarker.mp4 = 'https://vrmaker.istaging.com/00000photoUpload/taiwantrade/taiwantradeVR.mp4'
        this.setPopupUrl(`${specificMarker.mp4}`)
        this.setPopupSizeConfig({ widthPercent })
        this.showPopup()
      }
      if (_.get(this.currentBuilding, 'objectId', '') === 'f61536ff-5b81-49fd-ba52-8564c2fb8ad7') {
        const specificMarker = _.find(allMarkers, marker =>
          _.get(marker, 'objectId', '') === 'fcb09ce3-1f15-4895-9cf4-b9b7a859112f')
        specificMarker.mp4 = 'https://cdn-images-dev.istaging.com/multimedia/1228-2.mp4'
        this.setPopupUrl(`${specificMarker.mp4}`)
        this.setPopupSizeConfig({ widthPercent: 98 })
        this.showPopup()
      }
    },

    showIntro () {
      const introAssetsEnable = this.currentBuilding.introAssetsEnable
      if (!introAssetsEnable) {
        return
      }
      const introAsset = this.currentBuilding.introAssetsAttribute
      const type = introAsset.type
      const url = introAsset[type]
      if (!url) {
        return
      }
      this.setPopupUrl(url)
      this.setPopupSizeConfig({
        type: 'intro',
        widthPercent: 100
      })
      this.showPopup()
    },

    resolve (instanceName, ...args) {
      PromiseService.resolve(instanceName, ...args)
    },

    emit (eventName, ...args) {
      this.krpanoHooks.emit(eventName, ...args)
    }
  }
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/common/extends'
@import '~css/addon/main'

.krpano-wrapper {
  @extend .absolute-full
  z-index: $krpano-wrapper-z
}
</style>
